import localFont from 'next/font/local';

import { theme as breitlingBaseTheme } from '@/theme';
import {
  DEFAULT_DESKTOP_HEADER_HEIGHT,
  DEFAULT_MOBILE_HEADER_HEIGHT,
} from '@/theme/galletTheme/dimensions';
import { generateSpacings } from '@/theme/utils';

import { fontWeights } from '../fontWeights';
import { space } from '../space';
import type { GridContainer, Theme } from '../types';

const primary = '#3E4827';
const primaryLight = '#F68D2E';
const colors = {
  ...breitlingBaseTheme.colors,
  primary: primary,
  primaryDark: 'purple',
  primaryLight: primaryLight,
  secondary: '#F68D2E',
  darkBlue: '#0e2240',
  heading: '#F68D2E',
};

const moderat = localFont({
  src: [
    {
      path: '../../../public/fonts/moderat/Moderat-Bold.woff2',
      weight: '700',
      style: 'normal',
    },
    {
      path: '../../../public/fonts/moderat/Moderat-Regular.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: '../../../public/fonts/moderat/Moderat-Extrabold.woff2',
      weight: '200',
      style: 'normal',
    },
  ],
  display: 'swap',
});

const gridContainer: GridContainer = {
  xs: space.s100,
  sm: space.s100,
  md: space.s800,
  lg: space.s800,
  xl: space.s800,
};

const fontFamilies = {
  ...breitlingBaseTheme.fontFamilies,
  moderat: moderat.style.fontFamily,
};

const fontSizes = {
  ...breitlingBaseTheme.fontSizes,
  p1: '1.25rem', // 20px
  p2: '1.125rem', // 18px
  h1Mobile: '2.625rem', // 42px
  h1: '4rem', // 64px
  h2: '3.125rem', // 50px
  h3: '2.125rem', // 34px
  h4: '1.75rem', // 28px
};

const lineHeights = {
  ...breitlingBaseTheme.lineHeights,
  p2: '1.5rem',
};

export const galletTheme: Theme = {
  ...breitlingBaseTheme,
  colors: colors,
  fontSizes: fontSizes,
  fontFamilies: fontFamilies,
  lineHeights: lineHeights,
  gridContainer: gridContainer,
  fonts: {
    paragraphFont: moderat.style.fontFamily,
    subtitleFont: moderat.style.fontFamily,
    titleFont: moderat.style.fontFamily,
  },
  components: {
    ...breitlingBaseTheme.components,
    navigation: {
      height: '2.5rem',
      list: {
        gap: space.s600,
        paddingRight: space.s150,
      },
      marginLeft: space.s350,
    },
    container: {
      paddingInline: '1rem',
    },
    heading: {
      blockWidth: '7.875rem',
      blockBorder: '7px',
      blockMargin: '2rem',
      highlightBlock: 'block',
      fontWeight: '400',
      paddingBottom: '.5rem',
    },
    heroBanner: {
      title: {
        padding: '0 0 5% 160px',
      },
    },
    cmsCard: {
      content: {
        desktopMaxWidth: {
          Md: '806px',
          MediaBannerOrMediaAndBottomText: '560px',
          MediaAndSideTextOrSectionBanner: '480px',
          Default: '',
        },
      },
      baseCard: {
        mobile: {
          padding: '0',
          paddingHasWrapperMinHeight: '0',
        },
      },
    },
    header: {
      ...breitlingBaseTheme.components.header,
      defaultBackground: 'halfTransparent',
      megamenu: {
        fontSize: '.875rem',
      },
      theme: {
        white: {
          default: {
            icon: {
              color: primaryLight,
            },
            menu: {
              color: primary,
            },
            text: {
              color: primaryLight,
            },
          },
          hover: {
            icon: {
              color: primary,
            },
            text: {
              color: primaryLight,
            },
            headerInfoText: {
              color: primaryLight,
            },
          },
          active: {
            menu: {
              color: primary,
            },
            text: {
              color: primaryLight,
            },
          },
          accessibilityIcon: {
            path1: {
              color: primaryLight,
            },
            path2: {
              color: colors.white,
            },
          },
        },
        default: {
          default: {
            icon: {
              color: colors.white,
            },
            menu: {
              color: colors.white,
            },
            text: {
              color: colors.white,
            },
          },
          hover: {
            icon: {
              color: primaryLight,
            },
            text: {
              color: primaryLight,
            },
            headerInfoText: {
              color: primaryLight,
            },
          },
          active: {
            menu: {
              color: primaryLight,
            },
            text: {
              color: primaryLight,
            },
          },
          accessibilityIcon: {
            path1: {
              color: primaryLight,
            },
            path2: {
              color: colors.white,
            },
          },
        },
      },
      wrapper: {
        height: {
          mobile: `${DEFAULT_MOBILE_HEADER_HEIGHT}px`,
          regular: `${DEFAULT_DESKTOP_HEADER_HEIGHT}px`,
          shrunk: '3rem',
        },
      },
    },
    footer: {
      breakpoint: '72.5rem',
      background: primary,
      innerWrapper: {
        maxWidth: '90rem',
        paddingInline: `${space.s250}`,
        paddingInlineMobile: '1rem 0',
        paddingMobile: `46px 0 ${space.s100} 0`,
        padding: '34px 40px 0 40px',
      },
      wrapper: {
        height: {
          mobile: '5.5rem',
          regular: '4rem',
          shrunk: '3rem',
        },
      },
      break: {
        margin: `25px 0 30px 0`,
        marginMobile: `0 0 ${space.s150} 0`,
      },
      tail: {
        height: `${space.s050}`,
        background: primaryLight,
      },
      links: {
        margin: `0`,
        marginMobile: `30px 0 42px 0`,
        hoverColor: `${primaryLight}`,
      },
      bottomBox: {
        padding: '0',
        paddingMobile: '0',
        margin: '30px 0',
        marginMobile: `42px 0 0 0`,
      },
      menus: {
        orderMobile: {
          links: 1,
          copyRight: 2,
          break: 0,
        },
      },
      subscribe: {
        hoverColor: primaryLight,
      },
      BottomItemsWrapper: {
        fontSize: '.75rem',
      },
    },
    button: {
      borderWidth: 2,
      colorVariant: {
        ...breitlingBaseTheme.components.button.colorVariant,
        primary: {
          filled: {
            bg: colors.primary,
            color: colors.white,
            disabledColor: colors.white,
            hoverColor: colors.primary,
            hoverBg: colors.white,
          },
          outlined: {
            color: colors.primary,
            hoverBg: colors.primary,
            hoverBorderColor: colors.primary,
            hoverColor: colors.white,
            disabledColor: colors.primary,
            disabledBorderColor: colors.primary,
            disabledBg: colors.white,
          },
          link: {
            color: colors.primary,
            hoverColor: colors.primaryDark,
            disabledColor: colors.secondary,
          },
        },
      },
    },
    modal: {
      wrapper: { boxShadow: '0 0 1.25rem #0000009a', width: '45rem' },
      header: {
        color: colors.black,
        borderWidth: '1px',
      },
      tail: {
        height: `${space.s050}`,
        background: primaryLight,
      },
    },
    radio: {
      borderWidth: 2,
      checkedBorderWidth: 6,
      markColor: colors.primaryLight,
    },
    checkbox: {
      ...breitlingBaseTheme.components.checkbox,
      icon: {
        height: 18,
        width: 20,
        color: colors.white,
      },
      markColor: primaryLight,
    },
    newsLetter: {
      loaderVariant: 'primaryLight',
      button: {
        variant: 'outlined',
        icon: {
          name: 'longArrow',
          size: 's',
          width: '45',
        },
        style: {
          paddingLeft: '30px',
          paddingRight: '30px',
          width: '200px',
        },
      },
      visibleTail: true,
      successMessage: {
        textAlign: 'left',
        textAlignMobile: 'center',
        margin: generateSpacings(0, 6, 12, 6),
        marginMobile: space.s200,
        fontSize: '1rem',
        titleColor: primary,
        titleFontSize: '1.625rem',
        titleFontWeight: '600',
        mailtoTextDecoration: 'none',
      },
      formFontSize: '0.875rem',
      formLineHeight: '1rem',
      footerFontSize: '0.75rem',
      footerLineHeight: '120%',
      descFontSize: '0.875rem',
      descLineHeight: '120%',
      titleFontSize: '1.625rem',
      titleFontWeight: `${fontWeights.extraLight}`,
      subscribedHeaderDividerHidden: true,
    },
    select: {
      ...breitlingBaseTheme.components.select,
      variantStyles: {
        default: {
          menu: (styles) => ({
            ...styles,
            width: 'max-content',
            minWidth: '100%',
          }),
          option: (styles) => ({
            ...styles,
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
          }),
        },
      },
    },
  },
};
